import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { AttendanceApi, AttendanceStatus, StudentAttendanceDailyRow, StudentsApi } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useDefaultYear } from './useDefaultYear';
import { formatDate } from '@/lib/format';

const STATUS_TONE: Record<AttendanceStatus, 'green' | 'red' | 'amber' | 'blue' | 'slate'> = {
  present: 'green',
  absent: 'red',
  late: 'amber',
  half_day: 'blue',
  holiday: 'slate',
};

const STATUS_LABEL: Record<AttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  late: 'Late',
  half_day: 'Half Day',
  holiday: 'Holiday',
};

const columnHelper = createColumnHelper<StudentAttendanceDailyRow>();

const COLUMNS = [
  columnHelper.accessor('date', { header: 'Date', cell: (ctx) => formatDate(ctx.getValue()) }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (ctx) => <Badge tone={STATUS_TONE[ctx.getValue()]}>{STATUS_LABEL[ctx.getValue()]}</Badge>,
  }),
];

/** One student's full day-by-day attendance history over a range — not just totals. Same StudentAttendanceService.dailyList() query as the Report page, filtered to one studentId. */
export function DetailedStudentAttendancePage() {
  const dt = useDataTable({ defaultLimit: 50, defaultSortBy: 'date', defaultSortOrder: 'asc' });
  const { selectedYear, isLoading: yearLoading } = useDefaultYear();

  const [studentId, setStudentId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (selectedYear) {
      setStartDate(selectedYear.startDate.slice(0, 10));
      setEndDate(selectedYear.endDate.slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear?.id]);

  // NOT StudentsApi.lookup() — that still targets the old, pre-migration
  // entity shape and 500s against real schools (docs/DEVIATIONS.md
  // "repointing the students screens": only .list() was repointed so
  // far). .list() is the working, migrated-data-backed endpoint.
  const { data: studentsPage } = useQuery({
    queryKey: ['students', 'list-for-detailed-attendance'],
    queryFn: () => StudentsApi.list({ limit: 100 }),
  });
  const students = studentsPage?.items ?? [];

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['attendance', 'students-daily-detailed', dt.params, studentId, startDate, endDate],
    queryFn: () => AttendanceApi.studentsDaily({ ...dt.params, studentId, startDate: startDate || undefined, endDate: endDate || undefined }),
    enabled: !yearLoading && !!studentId && !!startDate && !!endDate,
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader title="Detailed Student Attendance" description="One student's full attendance history over a date range — every date and status, not just totals." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-64" value={studentId} onChange={(e) => setStudentId(e.target.value)}>
          <option value="">— Select a student —</option>
          {students.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name ?? '(no linked user)'} ({s.admissionNo})
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      {!studentId ? (
        <div className="card p-8 text-center text-slate-500">Select a student to view their attendance history.</div>
      ) : (
        <DataTable
          columns={COLUMNS}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No attendance recorded for this student in the selected range."
          getRowId={(row) => row.attendanceId}
        />
      )}
    </>
  );
}
